import * as React from "react";
import { styled } from "@mui/material/styles";
import { useMediaQuery, useTheme } from "@mui/material";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import AppsIcon from "@mui/icons-material/Apps";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import CommentIcon from "@mui/icons-material/Comment";
import EmailIcon from "@mui/icons-material/Email";
import SettingIcon from "@mui/icons-material/Settings";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import PeopleIcon from "@mui/icons-material/People";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import HomeIcon from "@mui/icons-material/Home";
import FactoryIcon from "@mui/icons-material/Factory";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AddchartOutlined,
  AnalyticsOutlined,
  LanguageOutlined,
  ReportProblemOutlined,
  RequestQuoteOutlined,
  TopicOutlined,
} from "@mui/icons-material";
import { isManage360 } from "../utils/helper";

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "drawerOpen",
})<{ drawerOpen?: boolean; }>(({ theme, drawerOpen }) => ({
  borderRadius: 10,
  margin: "4px 8px",
  padding: "10px 16px",
  minHeight: 44,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  justifyContent: drawerOpen ? "flex-start" : "center",
  [theme.breakpoints.down("sm")]: {
    margin: "2px 4px",
    padding: "8px 12px",
    minHeight: 40,
    borderRadius: 8,
  },
  ...(!drawerOpen && {
    margin: "4px",
    padding: "8px",
    minWidth: "auto",
    "& .MuiListItemText-root": {
      display: "none",
    },
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      margin: 0,
      justifyContent: "center",
    },
  }),
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(244, 162, 89, 0.08)",
    transform: drawerOpen ? "translateX(4px)" : "none",
    [theme.breakpoints.down("sm")]: {
      transform: drawerOpen ? "translateX(2px)" : "none",
    },
  },
  "&.Mui-selected": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(244, 162, 89, 0.24)"
        : "rgba(244, 162, 89, 0.12)",
    color: "#F4A259",
    fontWeight: 600,
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(244, 162, 89, 0.32)"
          : "rgba(244, 162, 89, 0.16)",
    },
    "& .MuiListItemIcon-root": {
      color: "#F4A259",
    },
  },
}));

const StyledNestedListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "drawerOpen",
})<{ drawerOpen?: boolean; }>(({ theme, drawerOpen }) => ({
  borderRadius: 8,
  margin: "2px 8px 2px 24px",
  padding: "8px 16px",
  minHeight: 40,
  position: "relative",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  [theme.breakpoints.down("sm")]: {
    margin: "2px 4px 2px 16px",
    padding: "6px 10px",
    minHeight: 36,
    borderRadius: 6,
  },
  ...(!drawerOpen && {
    margin: "2px 4px",
    padding: "8px",
    justifyContent: "center",
    "& .MuiListItemText-root": {
      display: "none",
    },
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      margin: 0,
      justifyContent: "center",
    },
    "&::before": {
      display: "none",
    },
  }),
  "&::before": {
    content: '""',
    position: "absolute",
    left: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.3)"
        : "rgba(0, 0, 0, 0.3)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    [theme.breakpoints.down("sm")]: {
      left: 4,
      width: "3px",
      height: "3px",
    },
  },
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(244, 162, 89, 0.05)",
    transform: "translateX(3px)",
    [theme.breakpoints.down("sm")]: {
      transform: "translateX(2px)",
    },
    "&::before": {
      backgroundColor: "#F4A259",
      width: "6px",
      height: "6px",
      [theme.breakpoints.down("sm")]: {
        width: "5px",
        height: "5px",
      },
    },
  },
  "&.Mui-selected": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(244, 162, 89, 0.16)"
        : "rgba(244, 162, 89, 0.08)",
    color: "#F4A259",
    fontWeight: 600,
    "&::before": {
      backgroundColor: "#F4A259",
      width: "6px",
      height: "6px",
      [theme.breakpoints.down("sm")]: {
        width: "5px",
        height: "5px",
      },
    },
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(244, 162, 89, 0.24)"
          : "rgba(244, 162, 89, 0.12)",
    },
    "& .MuiListItemIcon-root": {
      color: "#F4A259",
    },
  },
}));

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== "drawerOpen",
})<{ drawerOpen?: boolean; }>(({ theme, drawerOpen }) => ({
  minWidth: drawerOpen ? 40 : "auto",
  color: "inherit",
  justifyContent: drawerOpen ? "flex-start" : "center",
  "& svg": {
    fontSize: "1.25rem",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: drawerOpen ? 32 : "auto",
    "& svg": {
      fontSize: "1.1rem",
    },
  },
}));

const StyledCollapseList = styled(List)(({ theme }) => ({
  backgroundColor: "transparent",
  padding: "4px 0",
}));

interface SidebarMenuProps {
  open?: boolean;
  onClose?: () => void;
}

const SidebarMenu = ({ open = true, onClose }: SidebarMenuProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [configExpanded, setConfigExpanded] = React.useState(false);
  const [reportExpanded, setReportExpanded] = React.useState(false);
  const [socialExpanded, setSocialExpanded] = React.useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = React.useState(false);
  const [clientsExpanded, setClientsExpanded] = React.useState(false);
  const [jobsExpanded, setJobsExpanded] = React.useState(false);
  const [skillsExpanded, setSkillsExpanded] = React.useState(false);
  const [financesExpanded, setFinancesExpanded] = React.useState(false);
  const [industriesExpanded, setIndustriesExpanded] = React.useState(false);
  const _isManage360 = React.useMemo(() => isManage360(), []);

  const { pathname } = useLocation();
  const page = React.useMemo(() => {
    const parts = pathname.split("/");
    let page = "?";
    if (parts.length >= 3) page = parts[1] + "/" + parts[2];
    else if (parts.length >= 2) page = parts[1];
    return page;
  }, [pathname]);

  // Auto-expand collapse menus when on submenu pages
  React.useEffect(() => {
    if (page.startsWith("clients/")) {
      setClientsExpanded(true);
    }
    if (page.startsWith("jobs/")) {
      setJobsExpanded(true);
    }
    if (page.startsWith("skills/")) {
      setConfigExpanded(true);
      setSkillsExpanded(true);
    }
    if (page.startsWith("finances/")) {
      setReportExpanded(true);
      setFinancesExpanded(true);
    }
    if (page.startsWith("business-verticals/")) {
      setConfigExpanded(true);
      setIndustriesExpanded(true);
    }
  }, [page]);

  const handleNavigate = (path: string) => {
    if (isMobile && onClose) {
      onClose();
    }
    navigate(path);
  };

  return (
    <List
      component="nav"
      sx={{
        px: open ? { xs: 0.5, sm: 1 } : 0,
        py: { xs: 1, sm: 2 },
        mt: { xs: 0, sm: 0 },
        ...(open && isMobile && {
          pt: { xs: 2, sm: 0 },
        }),
        ...(!open && !isMobile && {
          pt: { xs: "56px", sm: "130px" },
        }),
      }}
    >
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => handleNavigate("/dashboard")}
        selected={page == "dashboard"}
        sx={{
          ...(open && isMobile && {
            mt: { xs: 1, sm: 0 },
          }),
        }}
      >
        <StyledListItemIcon drawerOpen={open}>
          <DashboardIcon />
        </StyledListItemIcon>
        <ListItemText
          primary="Dashboard"
          primaryTypographyProps={{
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
            fontWeight: page == "dashboard" ? 600 : 400,
          }}
        />
      </StyledListItemButton>
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setClientsExpanded(!clientsExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <BusinessIcon />
        </StyledListItemIcon>
        <ListItemText
          primary="Clients"
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {clientsExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={clientsExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/clients/workz360")}
            selected={page == "clients/workz360"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <BusinessIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Workz360"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/clients/home-edition")}
            selected={page == "clients/home-edition"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <BusinessIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Home Edition"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => handleNavigate("/talents")}
        selected={page == "talents"}
      >
        <StyledListItemIcon drawerOpen={open}>
          <PeopleIcon />
        </StyledListItemIcon>
        <ListItemText
          primary="Talents"
          primaryTypographyProps={{
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
            fontWeight: page == "talents" ? 600 : 400,
          }}
        />
      </StyledListItemButton>
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setJobsExpanded(!jobsExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <HomeRepairServiceIcon />
        </StyledListItemIcon>
        <ListItemText
          primary="Jobs"
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {jobsExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={jobsExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/jobs/workz360")}
            selected={page == "jobs/workz360"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <HomeRepairServiceIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Workz360"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/jobs/home-edition")}
            selected={page == "jobs/home-edition"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <HomeRepairServiceIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Home Edition"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>
      {!_isManage360 && (
        <>
          <StyledListItemButton
            drawerOpen={open}
            onClick={() => setReportExpanded(!reportExpanded)}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AddchartOutlined />
            </StyledListItemIcon>
            <ListItemText
              primary="Finance Menu"
              primaryTypographyProps={{
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
              }}
            />
            {reportExpanded ? <ExpandLess /> : <ExpandMore />}
          </StyledListItemButton>
          <Collapse in={reportExpanded} timeout="auto" unmountOnExit>
            <StyledCollapseList disablePadding>
              <StyledNestedListItemButton
                drawerOpen={open}
                onClick={() => setFinancesExpanded(!financesExpanded)}
                sx={{
                  pr: 0.5,
                  "& .MuiListItemText-root": {
                    minWidth: 0,
                    flex: "1 1 auto",
                    overflow: "hidden",
                  },
                  "& .MuiListItemText-root span": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <StyledListItemIcon drawerOpen={open}>
                  <AttachMoneyIcon fontSize="small" />
                </StyledListItemIcon>
                <ListItemText
                  primary="General Finances"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
                  }}
                />
                {financesExpanded ? <ExpandLess /> : <ExpandMore />}
              </StyledNestedListItemButton>
              <Collapse in={financesExpanded} timeout="auto" unmountOnExit>
                <StyledCollapseList disablePadding sx={{ pl: 2 }}>
                  <StyledNestedListItemButton
                    drawerOpen={open}
                    onClick={() => handleNavigate("/finances/workz360")}
                    selected={page == "finances/workz360"}
                  >
                    <StyledListItemIcon drawerOpen={open} sx={{ minWidth: 28 }}>
                      <BusinessIcon sx={{ fontSize: 18 }} />
                    </StyledListItemIcon>
                    <ListItemText
                      primary="Workz360"
                      primaryTypographyProps={{
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.825rem" },
                      }}
                    />
                  </StyledNestedListItemButton>
                  <StyledNestedListItemButton
                    drawerOpen={open}
                    onClick={() => handleNavigate("/finances/home-edition")}
                    selected={page == "finances/home-edition"}
                  >
                    <StyledListItemIcon drawerOpen={open} sx={{ minWidth: 28 }}>
                      <HomeIcon sx={{ fontSize: 18 }} />
                    </StyledListItemIcon>
                    <ListItemText
                      primary="Home Edition"
                      primaryTypographyProps={{
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.825rem" },
                      }}
                    />
                  </StyledNestedListItemButton>
                </StyledCollapseList>
              </Collapse>
              <StyledNestedListItemButton
                drawerOpen={open}
                onClick={() => handleNavigate("/reports/finance-report")}
                selected={page == "reports/finance-report"}
              >
                <StyledListItemIcon drawerOpen={open}>
                  <RequestQuoteOutlined fontSize="small" />
                </StyledListItemIcon>
                <ListItemText
                  primary="Financial Report"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
                  }}
                />
              </StyledNestedListItemButton>
              <StyledNestedListItemButton
                drawerOpen={open}
                onClick={() => handleNavigate("/withdrawals")}
                selected={page == "withdrawals"}
              >
                <StyledListItemIcon drawerOpen={open}>
                  <BusinessCenterIcon fontSize="small" />
                </StyledListItemIcon>
                <ListItemText
                  primary="Withdrawals"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
                  }}
                />
              </StyledNestedListItemButton>
            </StyledCollapseList>
          </Collapse>
        </>
      )}
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setConfigExpanded(!configExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <SettingIcon />
        </StyledListItemIcon>
        <ListItemText
          primary="Settings"
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {configExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={configExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => setIndustriesExpanded(!industriesExpanded)}
          >
            <StyledListItemIcon drawerOpen={open}>
              <FactoryIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Industries"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
            {industriesExpanded ? <ExpandLess /> : <ExpandMore />}
          </StyledNestedListItemButton>
          <Collapse in={industriesExpanded} timeout="auto" unmountOnExit>
            <StyledCollapseList disablePadding sx={{ pl: 2 }}>
              <StyledNestedListItemButton
                drawerOpen={open}
                onClick={() => handleNavigate("/business-verticals/workz360")}
                selected={page == "business-verticals/workz360"}
              >
                <StyledListItemIcon drawerOpen={open} sx={{ minWidth: 28 }}>
                  <BusinessIcon sx={{ fontSize: 18 }} />
                </StyledListItemIcon>
                <ListItemText
                  primary="Workz360"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.825rem" },
                  }}
                />
              </StyledNestedListItemButton>
              <StyledNestedListItemButton
                drawerOpen={open}
                onClick={() => handleNavigate("/business-verticals/home-edition")}
                selected={page == "business-verticals/home-edition"}
              >
                <StyledListItemIcon drawerOpen={open} sx={{ minWidth: 28 }}>
                  <HomeIcon sx={{ fontSize: 18 }} />
                </StyledListItemIcon>
                <ListItemText
                  primary="Home Edition"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.825rem" },
                  }}
                />
              </StyledNestedListItemButton>
            </StyledCollapseList>
          </Collapse>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => setSkillsExpanded(!skillsExpanded)}
          >
            <StyledListItemIcon drawerOpen={open}>
              <EngineeringIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Skills"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
            {skillsExpanded ? <ExpandLess /> : <ExpandMore />}
          </StyledNestedListItemButton>
          <Collapse in={skillsExpanded} timeout="auto" unmountOnExit>
            <StyledCollapseList disablePadding sx={{ pl: 2 }}>
              <StyledNestedListItemButton
                drawerOpen={open}
                onClick={() => handleNavigate("/skills/workz360")}
                selected={page == "skills/workz360"}
              >
                <StyledListItemIcon drawerOpen={open} sx={{ minWidth: 28 }}>
                  <BusinessIcon sx={{ fontSize: 18 }} />
                </StyledListItemIcon>
                <ListItemText
                  primary="Workz360"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.825rem" },
                  }}
                />
              </StyledNestedListItemButton>
              <StyledNestedListItemButton
                drawerOpen={open}
                onClick={() => handleNavigate("/skills/home-edition")}
                selected={page == "skills/home-edition"}
              >
                <StyledListItemIcon drawerOpen={open} sx={{ minWidth: 28 }}>
                  <HomeIcon sx={{ fontSize: 18 }} />
                </StyledListItemIcon>
                <ListItemText
                  primary="Home Edition"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.825rem" },
                  }}
                />
              </StyledNestedListItemButton>
            </StyledCollapseList>
          </Collapse>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/certifications")}
            selected={page == "certifications"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <CardMembershipIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Certifications"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/reasons")}
            selected={page == "reasons"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <CommentIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Reasons"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/email-templates")}
            selected={page == "email-templates"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <EmailIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Email Templates"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/app-updates")}
            selected={page == "app-updates"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AppsIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="App Updates"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/admins")}
            selected={page == "admins"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AdminPanelSettingsIcon fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Admin Users"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setSocialExpanded(!socialExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <DeviceHubIcon />
        </StyledListItemIcon>
        <ListItemText
          primary="Social Network"
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {socialExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={socialExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/social/feeds")}
            selected={page == "social/feeds"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <TopicOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Social Feeds"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/social/resources")}
            selected={page == "social/resources"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <LanguageOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Resources"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() => handleNavigate("/social/user-feedbacks")}
            selected={page == "social/user-feedbacks"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <ReportProblemOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="User Feedbacks"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>
      <StyledListItemButton
        drawerOpen={open}
        onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
      >
        <StyledListItemIcon drawerOpen={open}>
          <AnalyticsOutlined />
        </StyledListItemIcon>
        <ListItemText
          primary="Analytics"
          primaryTypographyProps={{
            fontWeight: 600,
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          }}
        />
        {analyticsExpanded ? <ExpandLess /> : <ExpandMore />}
      </StyledListItemButton>
      <Collapse in={analyticsExpanded} timeout="auto" unmountOnExit>
        <StyledCollapseList disablePadding>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() =>
              window.open(
                "https://analytics.google.com/analytics/web/?authuser=0#/p307958142/reports/"
              )
            }
            selected={page === "analytics"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AnalyticsOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="General"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() =>
              window.open(
                "https://analytics.google.com/analytics/web/?authuser=0#/p307958142/reports/dashboard?params=_u..nav%3Dmaui%26_r..dimension-value%3D%7B%22dimension%22:%22audienceName%22,%22value%22:%22All%20Users%22%7D%26_r.0..activeTab%3D1&r=audience-overview&collectionId=8614188802"
              )
            }
            selected={page === "analytics"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AnalyticsOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Platform"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
          <StyledNestedListItemButton
            drawerOpen={open}
            onClick={() =>
              window.open(
                "https://analytics.google.com/analytics/web/?authuser=0#/p307958142/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22screenPageViews%22%5D%26_r.explorerCard..seldim%3D%5B%22unifiedScreenName%22%5D&r=9784972620&collectionId=8614179004"
              )
            }
            selected={page === "analytics"}
          >
            <StyledListItemIcon drawerOpen={open}>
              <AnalyticsOutlined fontSize="small" />
            </StyledListItemIcon>
            <ListItemText
              primary="Tabs"
              primaryTypographyProps={{
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.875rem" },
              }}
            />
          </StyledNestedListItemButton>
        </StyledCollapseList>
      </Collapse>
    </List>
  );
};
export default SidebarMenu;
